import Head from 'next/head';
import React, { useContext, useEffect } from 'react';
import { qsStringify } from 'utils';
import { BrowseItemsContext } from '~/contexts/BrowseItemsContext';
import { usePageViewData } from '~/contexts/PageViewDataContext';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import { fireVisitedResults } from '~/services/analytics/events/search';
import absoluteUrl from '~/utils/absoluteUrl';
import generatePageTitle from '~/utils/generatePageTitle';

interface Props {}

const SearchMetadata: React.FC<Props> = () => {
  const facetFiltering = useFacetFiltering();
  const { items, paging } = useContext(BrowseItemsContext) || {};
  const pageViewData = usePageViewData();

  let title: string | undefined;

  const titleTag = facetFiltering.lander?.title_tag;
  if (facetFiltering.lander) {
    title =
      titleTag ||
      facetFiltering.lander.model?.display_name ||
      facetFiltering.lander.name;
  } else if (facetFiltering.filterState.q) {
    title = `Searching for "${facetFiltering.filterState.q}"`;

    if (facetFiltering.category) {
      title += ` in ${facetFiltering.category.name}`;
    }
  } else {
    title = 'Search';
  }

  if (paging.page > 1) {
    // Weird, but replace pipe with the page suffix
    if (title && title.includes(' | ')) {
      title = title.replace(' | ', ` - Page ${paging.page} | `);
    } else {
      title += ` - Page ${paging.page}`;
    }
  }

  // Only fire Visited Results when the category changes
  useEffect(() => {
    fireVisitedResults({
      facetFiltering,
      items,
      pageView: pageViewData?.pageView || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetFiltering.category?.id]);

  const generatedTitle = generatePageTitle(
    title,
    titleTag ? '' : 'New and Used on SidelineSwap',
  );

  return (
    <Head>
      <title>{generatedTitle}</title>
      <meta property="og:title" content={generatedTitle} />

      {/* Add a canonical to itself, allowing ?page parameters only */}
      {facetFiltering.lander ? (
        <link
          rel="canonical"
          href={absoluteUrl(
            `/shop/${facetFiltering.lander?.path}${qsStringify({
              page:
                paging?.page != null && paging.page > 1
                  ? paging.page
                  : undefined,
            })}`,
          )}
        />
      ) : null}

      {/*{(facetFiltering.paging?.page || 1) <= 6 && (*/}
      {/*  <meta name="robots" content="noindex" />*/}
      {/*)}*/}

      {facetFiltering.lander?.description && (
        <meta name="description" content={facetFiltering.lander.description} />
      )}
      {facetFiltering.lander?.description && (
        <meta
          property="og:description"
          content={facetFiltering.lander.description}
        />
      )}
    </Head>
  );
};

export default SearchMetadata;
